// The changed-file TypeScript check does not include the parcel ambient declarations automatically.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../../../declaration.d.ts" />

import AdvancedSearchDropdownConnected from '@src/apps/AdvancedSearchDropdown/AdvancedSearchDropdownConnected';
import ContentEditorDropdownConnected from '@src/apps/ContentEditorDropdown/ContentEditorDropdownConnected';
import uhcLogo from '@src/Assets/images/uhcBrandmark.png';
import { AiIcon } from '@src/Assets/svg/AiIcon';
import { MenuIcon } from '@src/Assets/svg/CommonAppHeaderIcons';
import { KnowledgeCenterIcon } from '@src/Assets/svg/KnowledgeCenterIcon';
import KnowledgeCenterCallWrapper from '@src/CommonComponents/KnowledgeCenterCallWrapper/KnowledgeCenterCallWrapper';
import { APP_EVENTS } from '@src/constants/app-shell-constants';
import { checkIsSimulationMode } from '@src/utility/createStorage';
import { StorageService } from '@src/utility/storage.service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { commonAppHeaderStyles } from './commonAppHeaderContent.styles';
import ProfileDropdown from './ProfileDropdown/ProfileDropdown';
import { MOBILE_NAV_BREAKPOINT } from '@src/generic-components/NavigationBar/NavigationBar';

type CurrentUserRecord = {
  UserPersonas?: { UserType?: string }[];
};

type CorporateTinSelection = {
  value?: string;
  Tin?: string;
};

type ProviderContextState = {
  uhc?: {
    selectedCorporateTin?: CorporateTinSelection;
  };
  selectedCorporateTin?: CorporateTinSelection;
};

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const hasMeaningfulValue = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return (
    normalized !== '' && normalized !== 'all' && normalized !== 'null' && normalized !== 'undefined'
  );
};

export const hasActiveProviderOrTinContext = (): boolean => {
  if (sessionStorage.getItem('simulationMode') === 'true') {
    return true;
  }

  const multiTinNavigation = sessionStorage.getItem('multiTinNavigation') === 'true';
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins') || '';
  const hasMultiTinSelection = multiSelectedTins.split(',').some((tin) => hasMeaningfulValue(tin));

  if (multiTinNavigation && hasMultiTinSelection) {
    return true;
  }

  if (hasMeaningfulValue(sessionStorage.getItem('PreviousOrg') || '')) {
    return true;
  }

  const state = readJson<ProviderContextState>('state', {});
  const selectedCorporateTin =
    state?.uhc?.selectedCorporateTin || state?.selectedCorporateTin || null;

  const tinValue = selectedCorporateTin?.value || selectedCorporateTin?.Tin || '';

  return hasMeaningfulValue(tinValue);
};

export const getAiGeniePath = (pathname: string): string => {
  const currentPath = pathname.toLowerCase();
  const isAiGenieHomeRoute =
    currentPath.includes('myhome') ||
    currentPath.includes('pype-home') ||
    currentPath.includes('medical-cost');

  return isAiGenieHomeRoute ? '/pype-home' : '/pype-entity';
};

export const CommonAppHeaderContent: React.FC = () => {
  const isMyHomeRoute = window.location.pathname.toLowerCase().includes('/myhome');
  const [isInternal, setIsInternal] = useState(() => {
    const parsedCurrentUser = readJson<CurrentUserRecord[]>('currentUser', []);
    return parsedCurrentUser?.[0]?.UserPersonas?.[0]?.UserType === 'Internal';
  });
  const [isSimulationMode, setIsSimulationMode] = useState(checkIsSimulationMode());
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= MOBILE_NAV_BREAKPOINT);
  const [isMenuButtonHovered, setIsMenuButtonHovered] = useState(false);
  const [isMenuButtonPressed, setIsMenuButtonPressed] = useState(false);
  const [isAiTooltipVisible, setIsAiTooltipVisible] = useState(false);
  const storageService = useMemo(() => new StorageService(), []);

  const syncSession = useCallback(() => {
    const parsedCurrentUser = readJson<CurrentUserRecord[]>('currentUser', []);
    const internalUser = parsedCurrentUser?.[0]?.UserPersonas?.[0]?.UserType === 'Internal';
    setIsInternal(internalUser);
    setIsSimulationMode(checkIsSimulationMode());
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      syncSession();
    }, 500);

    return () => {
      window.clearInterval(timer);
    };
  }, [syncSession]);

  useEffect(() => {
    const syncMobileState = () => {
      const mobile = window.innerWidth <= MOBILE_NAV_BREAKPOINT;
      setIsMobileView(mobile);
    };

    syncMobileState();
    window.addEventListener('resize', syncMobileState);

    return () => {
      window.removeEventListener('resize', syncMobileState);
    };
  }, []);

  const onHamburgerClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent(APP_EVENTS.OPEN_RESPONSIVE_HAM_MENU));
  }, []);

  const onLogoClick = useCallback(() => {
    storageService.setAppEventDataLayerForAdobeOnInteraction({
      interactionName: 'header',
      interactionValue: 'UHC Logo',
      interactionContext: 'header logo click',
    });
    const currentUrl = window.location.pathname.toLowerCase();
    const isPsms = currentUrl.includes('psms');

    // Route based on user type
    let targetPath = '/myhome';
    if (isInternal) {
      // Angular parity: internal users go to overview only when provider/TIN context is active.
      if (hasActiveProviderOrTinContext()) {
        targetPath = '/overview-page-advocate';
      } else {
        targetPath = '/myhome';
      }
    } else {
      targetPath = '/overview-page';
    }

    const finalPath = isPsms ? `/PSMS${targetPath}` : targetPath;
    window.location.href = finalPath;
  }, [storageService, isInternal]);

  const onAiClick = useCallback(() => {
    setIsAiTooltipVisible(false);
    sessionStorage.setItem('aiGenieParentSource', window.location.pathname);
    sessionStorage.removeItem('aiMetricFlow');
    storageService.setAppEventDataLayerForAdobeOnInteraction({
      interactionName: 'header',
      interactionValue: 'AI Genie',
      interactionContext: 'AI link click',
    });
    window.location.href = getAiGeniePath(window.location.pathname);
  }, [storageService]);

  return (
    <div style={commonAppHeaderStyles.root}>
      <div
        style={{
          ...commonAppHeaderStyles.header,
          ...(isMobileView ? commonAppHeaderStyles.headerMobile : {}),
        }}
      >
        {isMobileView && !isMyHomeRoute && (
          <div style={commonAppHeaderStyles.menuWrap}>
            <button
              type="button"
              aria-label="Open navigation menu"
              style={{
                ...commonAppHeaderStyles.menuButton,
                ...(isMenuButtonHovered ? commonAppHeaderStyles.menuButtonHover : {}),
                ...(isMenuButtonPressed ? commonAppHeaderStyles.menuButtonActive : {}),
              }}
              onMouseEnter={() => setIsMenuButtonHovered(true)}
              onMouseLeave={() => {
                setIsMenuButtonHovered(false);
                setIsMenuButtonPressed(false);
              }}
              onMouseDown={() => setIsMenuButtonPressed(true)}
              onMouseUp={() => setIsMenuButtonPressed(false)}
              onClick={onHamburgerClick}
            >
              <MenuIcon />
            </button>
          </div>
        )}
        <div style={commonAppHeaderStyles.logoWrap} onClick={onLogoClick}>
          <img
            style={commonAppHeaderStyles.logo}
            src={uhcLogo}
            alt="UHC Logo"
            loading="eager"
            decoding="sync"
          />
        </div>

        <div style={commonAppHeaderStyles.advancedContainer}>
          <div style={commonAppHeaderStyles.advancedActions}>
            <AdvancedSearchDropdownConnected />
          </div>
        </div>

        <div style={commonAppHeaderStyles.right}>
          <ContentEditorDropdownConnected />

          <div style={commonAppHeaderStyles.helpWrap}>
            {isInternal && !isSimulationMode && (
              <div
                style={commonAppHeaderStyles.aiBlock}
                onClick={onAiClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    onAiClick();
                  }

                  if (event.key === ' ') {
                    event.preventDefault();
                    onAiClick();
                  }
                }}
                onMouseEnter={() => setIsAiTooltipVisible(true)}
                onMouseLeave={() => setIsAiTooltipVisible(false)}
                onFocus={() => setIsAiTooltipVisible(true)}
                onBlur={() => setIsAiTooltipVisible(false)}
                onMouseDown={() => setIsAiTooltipVisible(false)}
                onTouchStart={() => setIsAiTooltipVisible(false)}
                tabIndex={0}
                role="button"
                aria-label="Open AI Genie"
              >
                <span
                  style={{
                    ...commonAppHeaderStyles.aiTooltip,
                    ...(isAiTooltipVisible
                      ? commonAppHeaderStyles.aiTooltipVisible
                      : commonAppHeaderStyles.aiTooltipHidden),
                  }}
                >
                  <span style={commonAppHeaderStyles.aiTooltipContent}>UHC insights Assistant</span>
                  <span style={commonAppHeaderStyles.aiTooltipContent}>Chat to explore UHC</span>
                  <span style={commonAppHeaderStyles.aiTooltipContent}>Insights data.</span>
                  <span style={commonAppHeaderStyles.aiTooltipArrow} />
                </span>
                <div style={commonAppHeaderStyles.helpIcon} aria-hidden="true">
                  <AiIcon />
                </div>
              </div>
            )}

            <KnowledgeCenterCallWrapper metricId="ALL" title="ALL">
              <div style={commonAppHeaderStyles.kcBlock}>
                <KnowledgeCenterIcon />
                <span style={commonAppHeaderStyles.kcText}>Knowledge Center</span>
              </div>
            </KnowledgeCenterCallWrapper>
          </div>

          <ProfileDropdown />
        </div>
      </div>
    </div>
  );
};

export default CommonAppHeaderContent;
